"use client";

import { useState, useRef, type ReactElement } from "react";
import { Check, SkipForward, Clock, Navigation, ExternalLink, ChevronDown, Trophy, Car, Target, DoorOpen, Tag, AlertTriangle, Eye, Plus, Loader2, Pencil } from "lucide-react";
import { Button } from "@/app/_module/components/ui/button";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/app/_module/components/ui/dropdown-menu";
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from "@/app/_module/components/ui/tooltip";
import getStatusColor from "@/app/(authenticated)/leads/_modules/constants/get-status-color";
import type { StopDetail } from "../types/types";

// Native HTML `title` tooltips have a 1-2s delay, fail on touch, and don't
// always fire when the SVG is the hover target. This wraps a single child
// in a Radix tooltip that shows immediately and renders consistently. Use
// for any icon-only button where the agent needs to learn the action.
function Tip({ label, children }: { label: string; children: ReactElement }) {
  return (
    <Tooltip>
      <TooltipTrigger asChild>{children}</TooltipTrigger>
      <TooltipContent side="top" className="text-[11px] py-1 px-2">{label}</TooltipContent>
    </Tooltip>
  );
}

interface RouteLeadListProps {
  stops: StopDetail[];
  activeStopIndex: number;
  // Fired when the row body is tapped — used to set the active stop +
  // zoom the map. Should NOT open the lead detail panel; that's the eye
  // icon's job (onViewLead).
  onStopClick: (leadId: string, index: number) => void;
  // Fired only by the eye icon — opens the lead detail panel.
  onViewLead?: (leadId: string, index: number) => void;
  onMarkVisited: (leadId: string) => void;
  onMarkSkipped: (leadId: string) => void;
  onMarkNotHome?: (leadId: string) => void;
  onSetFieldTag?: (leadId: string, tag: string) => void;
  onUpdatePipeline?: (leadId: string, status: string) => void;
  // Tap "Check address" badge → re-geocode this lead via Nominatim+Census+
  // Photon cross-validation. Returns whether the fix succeeded so the badge
  // can show a loading state and disappear on success.
  onAutoFixPin?: (leadId: string) => Promise<boolean>;
  totalDistanceMeters: number;
  totalDurationSeconds: number;
  routeStatus?: "planned" | "in_progress" | "completed";
  startedAt?: string;
  completedAt?: string;
  knockStats?: { totalKnocks: number; notHome: number; conversations: number; appointments: number; sold: number };
  duplicateLeadIds?: Set<string>;
  appointmentTimes?: Record<string, string>;
  // Lead currently in pin-edit mode (drag-to-fix open on the map). Sidebar
  // mirrors the state with a pencil icon + amber row tint so the agent
  // never loses track of which stop they're correcting.
  editingPinForLeadId?: string | null;
}

const PIPELINE_OPTIONS = [
  { value: "Appt", label: "Appt" },
  { value: "Underwriting", label: "Underwriting" },
  { value: "Sold & Approved", label: "Sold" },
  { value: "No sale", label: "No Sale" },
  { value: "Bad number", label: "Bad #" },
  { value: "Declined", label: "Declined" },
];

const FIELD_TAGS = [
  { value: "interested", label: "Interested", color: "bg-emerald-50 text-emerald-700" },
  { value: "callback", label: "Callback", color: "bg-amber-50 text-amber-700" },
  { value: "not_home", label: "Not Home", color: "bg-blue-50 text-blue-700" },
  { value: "no_soliciting", label: "No Soliciting", color: "bg-red-50 text-red-600" },
  { value: "gated", label: "Gated", color: "bg-gray-100 text-gray-600" },
  { value: "dog", label: "Dog", color: "bg-orange-50 text-orange-600" },
];

function isAppleDevice(): boolean {
  if (typeof navigator === "undefined") return false;
  const ua = navigator.userAgent || "";
  // Modern iPads identify as Mac in UA but expose touch — combo detects both.
  const isIPadMac = /Macintosh/.test(ua) && (navigator as any).maxTouchPoints > 1;
  return /iPad|iPhone|iPod/.test(ua) || isIPadMac;
}

// Build a US-postal-formatted address string. Both Apple and Google
// geocoders are picky: a comma between state and ZIP (`MO, 63935`) gets
// parsed as two separate fields and rejected for rural addresses. The
// canonical USPS-style format is `street, city, ST ZIP, USA`.
function formatPostalAddress(stop: { address?: string; city?: string; state?: string; zipCode?: string }): string {
  const street = (stop.address || "").trim();
  const city = (stop.city || "").trim();
  const state = (stop.state || "").trim();
  const zip = (stop.zipCode || "").trim();
  const stateZip = [state, zip].filter(Boolean).join(" ");
  return [street, city, stateZip, "USA"].filter(Boolean).join(", ");
}

function openAppleMaps(stop: StopDetail) {
  // Send the lead's address string verbatim — Apple Maps geocodes it on
  // the device and shows it as the destination card label, matching what
  // the agent sees in the lead detail UI.
  const address = formatPostalAddress(stop);
  if (!address) return;
  window.open(`https://maps.apple.com/?daddr=${encodeURIComponent(address)}`, "_blank");
}

function openGoogleMaps(stop: StopDetail) {
  // Send the lead's address string verbatim — Google geocodes it and
  // shows it as the destination, matching the lead detail UI.
  // Use the LEGACY `maps.google.com/maps?daddr=` URL instead of the
  // newer `dir/?api=1&destination=` URL. The legacy URL falls back to
  // a fuzzy search when an address can't be exactly matched (common
  // for rural / unusual / typo'd addresses), instead of failing hard
  // with "can't find that address — try adding a city, state, or ZIP".
  const address = formatPostalAddress(stop);
  if (!address) return;
  const dest = encodeURIComponent(address);
  const webUrl = `https://maps.google.com/maps?daddr=${dest}`;

  // On iOS the universal Google URL doesn't auto-launch the Google Maps app —
  // try the comgooglemaps:// scheme first. If the app isn't installed, the
  // scheme silently no-ops, and we fall back to the web URL after a beat.
  if (isAppleDevice()) {
    const appUrl = `comgooglemaps://?daddr=${dest}&directionsmode=driving`;
    const start = Date.now();
    // Hidden navigation: assigning location triggers the scheme without
    // leaving a 404 tab if the app isn't installed.
    window.location.href = appUrl;
    setTimeout(() => {
      // If we're still on this page after 1.2s, the scheme didn't take —
      // fall back to the web map in a new tab.
      if (Date.now() - start < 2000 && document.visibilityState === "visible") {
        window.open(webUrl, "_blank");
      }
    }, 1200);
    return;
  }

  // Android + desktop — `google.com/maps/dir` triggers the Google Maps app
  // via Android intents and opens normally on desktop.
  window.open(webUrl, "_blank");
}

function formatDistance(meters: number): string {
  const miles = meters / 1609.34;
  return miles < 0.1 ? `${Math.round(meters)} ft` : `${miles.toFixed(1)} mi`;
}

function formatDuration(seconds: number): string {
  if (seconds < 60) return `${seconds}s`;
  const mins = Math.round(seconds / 60);
  if (mins < 60) return `${mins} min`;
  const hrs = Math.floor(mins / 60);
  const remainMins = mins % 60;
  return remainMins > 0 ? `${hrs}h ${remainMins}m` : `${hrs}h`;
}

export function RouteLeadList({
  stops,
  activeStopIndex,
  onStopClick,
  onViewLead,
  onMarkVisited,
  onMarkSkipped,
  onMarkNotHome,
  onSetFieldTag,
  onUpdatePipeline,
  onAutoFixPin,
  totalDistanceMeters,
  totalDurationSeconds,
  routeStatus,
  startedAt,
  completedAt,
  knockStats,
  duplicateLeadIds,
  appointmentTimes,
  editingPinForLeadId,
}: RouteLeadListProps) {
  const [expandedStopId, setExpandedStopId] = useState<string | null>(null);
  const [pipelineUpdates, setPipelineUpdates] = useState<Record<string, string>>({});
  const [autoFixingLeadId, setAutoFixingLeadId] = useState<string | null>(null);

  const visitedCount = stops.filter((s) => s.visited).length;
  const skippedCount = stops.filter((s) => s.skipped).length;
  const notHomeCount = stops.filter((s) => s.notHome && !s.visited && !s.skipped).length;
  const remaining = stops.length - visitedCount - skippedCount - notHomeCount;

  const handlePipelineSelect = (leadId: string, status: string) => {
    setPipelineUpdates((prev) => ({ ...prev, [leadId]: status }));
    onUpdatePipeline?.(leadId, status);
    setExpandedStopId(null);
  };

  // Completion summary
  if (routeStatus === "completed") {
    // Use actual time if route ran for more than 5 min, otherwise use estimated drive time
    const wallClockSeconds = startedAt && completedAt
      ? Math.round((new Date(completedAt).getTime() - new Date(startedAt).getTime()) / 1000)
      : 0;
    const timeSpent = wallClockSeconds > 300 ? wallClockSeconds : totalDurationSeconds;

    const apptCount = Object.values(pipelineUpdates).filter((s) => s === "Appt").length;
    const soldCount = Object.values(pipelineUpdates).filter((s) => s === "Sold & Approved").length;
    const uwrCount = Object.values(pipelineUpdates).filter((s) => s === "Underwriting").length;
    const noSaleCount = Object.values(pipelineUpdates).filter((s) => s === "No sale").length;

    return (
      <div className="flex flex-col h-full">
        <div className="flex-1 overflow-y-auto p-5">
          {/* Header */}
          <div className="text-center mb-6">
            <div className="w-14 h-14 rounded-2xl bg-emerald-50 flex items-center justify-center mx-auto mb-3">
              <Trophy className="w-7 h-7 text-emerald-600" />
            </div>
            <h3 className="text-lg font-bold text-gray-900">Route Complete</h3>
            <p className="text-sm text-gray-500 mt-1">Here&apos;s how it went</p>
          </div>

          {/* Stats grid */}
          <div className="grid grid-cols-2 gap-3 mb-6">
            <div className="bg-gray-50 rounded-xl p-3 text-center">
              <Target className="w-4 h-4 text-gray-400 mx-auto mb-1" />
              <p className="text-xl font-bold text-gray-900">{stops.length}</p>
              <p className="text-[10px] text-gray-500 uppercase tracking-wider">Total Stops</p>
            </div>
            <div className="bg-emerald-50 rounded-xl p-3 text-center">
              <Check className="w-4 h-4 text-emerald-500 mx-auto mb-1" />
              <p className="text-xl font-bold text-emerald-700">{visitedCount}</p>
              <p className="text-[10px] text-gray-500 uppercase tracking-wider">Visited</p>
            </div>
            <div className="bg-gray-50 rounded-xl p-3 text-center">
              <Car className="w-4 h-4 text-gray-400 mx-auto mb-1" />
              <p className="text-xl font-bold text-gray-900">{formatDistance(totalDistanceMeters)}</p>
              <p className="text-[10px] text-gray-500 uppercase tracking-wider">Driven</p>
            </div>
            <div className="bg-gray-50 rounded-xl p-3 text-center">
              <Clock className="w-4 h-4 text-gray-400 mx-auto mb-1" />
              <p className="text-xl font-bold text-gray-900">{timeSpent > 0 ? formatDuration(timeSpent) : formatDuration(totalDurationSeconds)}</p>
              <p className="text-[10px] text-gray-500 uppercase tracking-wider">Time</p>
            </div>
          </div>

          {/* Pipeline outcomes */}
          {Object.keys(pipelineUpdates).length > 0 && (
            <div className="mb-6">
              <h4 className="text-xs font-semibold text-gray-500 uppercase tracking-wider mb-3">Outcomes</h4>
              <div className="space-y-2">
                {soldCount > 0 && (
                  <div className="flex items-center justify-between py-1.5">
                    <span className={`text-xs font-semibold px-2 py-0.5 rounded-full ${getStatusColor("Sold & Approved")}`}>Sold</span>
                    <span className="text-sm font-bold text-gray-900">{soldCount}</span>
                  </div>
                )}
                {apptCount > 0 && (
                  <div className="flex items-center justify-between py-1.5">
                    <span className={`text-xs font-semibold px-2 py-0.5 rounded-full ${getStatusColor("Appt")}`}>Appt</span>
                    <span className="text-sm font-bold text-gray-900">{apptCount}</span>
                  </div>
                )}
                {uwrCount > 0 && (
                  <div className="flex items-center justify-between py-1.5">
                    <span className={`text-xs font-semibold px-2 py-0.5 rounded-full ${getStatusColor("Underwriting")}`}>Underwriting</span>
                    <span className="text-sm font-bold text-gray-900">{uwrCount}</span>
                  </div>
                )}
                {noSaleCount > 0 && (
                  <div className="flex items-center justify-between py-1.5">
                    <span className={`text-xs font-semibold px-2 py-0.5 rounded-full ${getStatusColor("No sale")}`}>No Sale</span>
                    <span className="text-sm font-bold text-gray-900">{noSaleCount}</span>
                  </div>
                )}
                {skippedCount > 0 && (
                  <div className="flex items-center justify-between py-1.5">
                    <span className="text-xs font-semibold px-2 py-0.5 rounded-full bg-gray-100 text-gray-600">Skipped</span>
                    <span className="text-sm font-bold text-gray-900">{skippedCount}</span>
                  </div>
                )}
              </div>
            </div>
          )}

          {/* Optimized route note */}
          <div className="bg-primary/5 rounded-xl p-4 text-center">
            <p className="text-xs text-primary font-medium mb-1">Route was AI-optimized</p>
            <p className="text-sm text-gray-600">
              {formatDistance(totalDistanceMeters)} driven across {stops.length} stops
            </p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="flex flex-col h-full">
      {/* Daily knock stats bar */}
      {knockStats && knockStats.totalKnocks > 0 && (
        <div className="px-4 py-2 bg-primary/5 border-b border-primary/10 flex items-center justify-between">
          <div className="flex items-center gap-3 text-[11px] font-medium">
            <span className="text-gray-900">{knockStats.totalKnocks} doors</span>
            {knockStats.notHome > 0 && <span className="text-blue-600">{knockStats.notHome} not home</span>}
            <span className="text-gray-600">{knockStats.conversations} convos</span>
            {knockStats.appointments > 0 && <span className="text-emerald-600">{knockStats.appointments} appts</span>}
            {knockStats.sold > 0 && <span className="text-emerald-700 font-bold">{knockStats.sold} sold</span>}
          </div>
          <span className="text-[9px] text-gray-400">today</span>
        </div>
      )}

      {/* Route summary header */}
      <div className="px-4 py-3 border-b border-gray-100 bg-gray-50/50">
        <div className="grid grid-cols-3 gap-2 text-center">
          <div>
            <p className="text-xs text-gray-500 uppercase tracking-wider">Stops</p>
            <p className="text-lg font-semibold text-gray-900">{stops.length}</p>
          </div>
          <div>
            <p className="text-xs text-gray-500 uppercase tracking-wider">Distance</p>
            <p className="text-lg font-semibold text-gray-900">{formatDistance(totalDistanceMeters)}</p>
          </div>
          <div>
            <p className="text-xs text-gray-500 uppercase tracking-wider">Drive Time</p>
            <p className="text-lg font-semibold text-gray-900">{formatDuration(totalDurationSeconds)}</p>
          </div>
        </div>

        {/* Progress bar */}
        {(visitedCount > 0 || skippedCount > 0) && (
          <div className="mt-3">
            <div className="flex items-center justify-between text-xs text-gray-500 mb-1">
              <span>{visitedCount} visited{notHomeCount > 0 && `, ${notHomeCount} not home`}{skippedCount > 0 && `, ${skippedCount} skipped`}</span>
              <span>{remaining} remaining</span>
            </div>
            <div className="h-1.5 bg-gray-200 rounded-full overflow-hidden">
              <div
                className="h-full bg-emerald-500 rounded-full transition-all duration-300"
                style={{ width: `${((visitedCount + skippedCount + notHomeCount) / stops.length) * 100}%` }}
              />
            </div>
          </div>
        )}
      </div>

      {/* Stop list */}
      <div className="flex-1 overflow-y-auto">
        {stops.map((stop, i) => {
          const isActive = activeStopIndex === i;
          const isDone = stop.visited || stop.skipped;
          const isNotHome = stop.notHome;
          const isExpanded = expandedStopId === stop.leadId;
          const currentPipeline = pipelineUpdates[stop.leadId];
          const isDuplicate = duplicateLeadIds?.has(stop.leadId);
          const isEditing = editingPinForLeadId === stop.leadId;
          // Mirror the same condition the "Tap to fix" badge uses (line below)
          // so the whole row tints red when this stop has a bad pin. Visual
          // priority order: editing (amber) > needsFix (red) > active (primary
          // tint) > done (muted) > default. Editing wins because the agent
          // has explicitly engaged with the row and the amber pulse should
          // feel like the loudest signal in the list.
          const needsFix = stop.distanceFromPrevMeters > 80467
            && !(stop.visited || stop.skipped)
            && (stop as any).geocodeSource !== "manual";
          const currentTag = stop.fieldTag
            ? FIELD_TAGS.find(t => t.value === stop.fieldTag) || { value: stop.fieldTag, label: stop.fieldTag.replace(/_/g, " ").replace(/\b\w/g, c => c.toUpperCase()), color: "bg-gray-100 text-gray-700" }
            : null;

          return (
            <div
              key={stop.leadId}
              className={`
                relative border-b border-gray-100 transition-colors
                ${isEditing
                  ? "bg-amber-50 ring-2 ring-amber-300 ring-inset"
                  : needsFix
                    ? "bg-red-50/70 ring-2 ring-red-200 ring-inset"
                    : isActive ? "bg-primary/5" : isDone ? "bg-gray-50/50" : "hover:bg-gray-50"}
              `}
            >
              {/* Connector line — runs full height between circle centers */}
              {i < stops.length - 1 && (
                <div className="absolute left-[29px] top-[26px] bottom-0 w-[2px] bg-gradient-to-b from-primary/20 via-primary/10 to-primary/20" />
              )}
              {/* Top connector from previous stop */}
              {i > 0 && (
                <div className="absolute left-[29px] top-0 h-[14px] w-[2px] bg-primary/15" />
              )}

              <div
                className="flex items-start gap-3 px-4 py-3 cursor-pointer"
                onClick={() => onStopClick(stop.leadId, i)}
              >
                {/* Stop number badge */}
                <div
                  className={`
                    flex-shrink-0 w-7 h-7 rounded-full flex items-center justify-center text-xs font-bold z-10
                    ${
                      stop.visited
                        ? "bg-emerald-500 text-white"
                        : stop.skipped
                          ? "bg-gray-300 text-gray-600"
                          : isNotHome
                            ? "bg-amber-400 text-white"
                            : isActive
                              ? "bg-primary text-white ring-2 ring-primary/20"
                              : "bg-white border-2 border-primary/60 text-primary"
                    }
                  `}
                >
                  {stop.visited ? <Check className="w-3.5 h-3.5" /> : isNotHome ? <DoorOpen className="w-3.5 h-3.5" /> : stop.order}
                </div>

                {/* Stop info */}
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-1.5 flex-wrap">
                    <p className={`text-sm font-medium truncate ${isDone ? "text-gray-400 line-through" : isNotHome ? "text-amber-600" : "text-gray-900"}`}>
                      {stop.name || "Unknown"}
                    </p>
                    {isNotHome && !isDone && (
                      <span className="text-[9px] font-semibold px-1.5 py-0.5 rounded-full bg-amber-50 text-amber-600 flex-shrink-0">
                        Not Home
                      </span>
                    )}
                    {currentPipeline && (
                      <span className={`text-[9px] font-semibold px-1.5 py-0.5 rounded-full flex-shrink-0 ${getStatusColor(currentPipeline)}`}>
                        {currentPipeline === "Sold & Approved" ? "Sold" : currentPipeline}
                      </span>
                    )}
                    {currentPipeline === "Appt" && appointmentTimes?.[stop.leadId] && (() => {
                      const apptDate = new Date(appointmentTimes[stop.leadId]);
                      const now = new Date();
                      const isToday = apptDate.toDateString() === now.toDateString();
                      const tomorrow = new Date(now);
                      tomorrow.setDate(tomorrow.getDate() + 1);
                      const isTomorrow = apptDate.toDateString() === tomorrow.toDateString();
                      const timeStr = apptDate.toLocaleTimeString("en-US", { hour: "numeric", minute: "2-digit" });
                      const label = isToday ? `Today ${timeStr}` : isTomorrow ? `Tomorrow ${timeStr}` : apptDate.toLocaleDateString("en-US", { month: "short", day: "numeric" }) + ` ${timeStr}`;
                      return (
                        <span className="text-[9px] font-medium px-1.5 py-0.5 rounded-full bg-blue-50 text-blue-600 flex-shrink-0 flex items-center gap-0.5">
                          <Clock className="w-2 h-2" />
                          {label}
                        </span>
                      );
                    })()}
                    {currentTag && (
                      <span className={`text-[9px] font-semibold px-1.5 py-0.5 rounded-full flex-shrink-0 ${currentTag.color}`}>
                        {currentTag.label}
                      </span>
                    )}
                    {isDuplicate && !isDone && (
                      <span className="text-[9px] font-semibold px-1.5 py-0.5 rounded-full bg-yellow-50 text-yellow-700 flex-shrink-0 flex items-center gap-0.5">
                        <AlertTriangle className="w-2 h-2" />
                        Knocked
                      </span>
                    )}
                    {/* Outlier badge — leg > 50 miles is almost always a bad
                        pin. Hidden once geocodeSource === 'manual' since the
                        agent has already hand-corrected this stop (via drag
                        OR by typing the address override) and we trust their
                        placement even if the leg is still long (e.g. they
                        really do drive 60 miles to the next county). The
                        badge replaces itself with a loading spinner during
                        the re-check fetch. Same condition tints the row red. */}
                    {needsFix && (
                      <button
                        type="button"
                        onClick={async (e) => {
                          e.stopPropagation();
                          if (!onAutoFixPin || autoFixingLeadId === stop.leadId) return;
                          setAutoFixingLeadId(stop.leadId);
                          try { await onAutoFixPin(stop.leadId); }
                          finally { setAutoFixingLeadId(null); }
                        }}
                        disabled={autoFixingLeadId === stop.leadId}
                        className="text-[9px] font-semibold px-1.5 py-0.5 rounded-full bg-red-50 text-red-700 hover:bg-red-100 flex-shrink-0 flex items-center gap-0.5 disabled:opacity-60 transition-colors"
                        title="This stop looks far off — tap to re-check the address"
                      >
                        {autoFixingLeadId === stop.leadId ? (
                          <>
                            <Loader2 className="w-2 h-2 animate-spin" />
                            Fixing…
                          </>
                        ) : (
                          <>
                            <AlertTriangle className="w-2 h-2" />
                            Tap to fix
                          </>
                        )}
                      </button>
                    )}
                  </div>
                  <p className="text-xs text-gray-500 truncate mt-0.5">
                    {[stop.address, stop.city, stop.state].filter(Boolean).join(", ")}
                  </p>

                  {/* Drive from previous (or from start for first stop) */}
                  {stop.distanceFromPrevMeters > 0 && (
                    <div className="flex items-center gap-2 mt-1">
                      <span className="inline-flex items-center gap-1 text-[10px] text-gray-400">
                        <Navigation className="w-2.5 h-2.5" />
                        {formatDistance(stop.distanceFromPrevMeters)}
                      </span>
                      <span className="inline-flex items-center gap-1 text-[10px] text-gray-400">
                        <Clock className="w-2.5 h-2.5" />
                        {formatDuration(stop.durationFromPrevSeconds)}
                      </span>
                    </div>
                  )}

                {/* Action buttons — moved inside the stop-info column and
                    rendered as a SECOND ROW under the address, so the
                    name + address get the full sidebar width instead of
                    fighting 7 icons for 60px. -ml-1 nudges the icons back
                    into the same horizontal alignment as the name. */}
                <div className="flex items-center gap-1 mt-1.5 -ml-1 flex-wrap">
                  <Tip label="View lead details">
                    <Button
                      variant="ghost"
                      size="sm"
                      className="h-7 w-7 p-0 text-blue-500 hover:bg-blue-50"
                      onClick={(e) => {
                        e.stopPropagation();
                        // Eye icon is the only way to open the lead detail
                        // panel from the route. Row-body click just sets the
                        // active stop + zooms the map.
                        (onViewLead || onStopClick)(stop.leadId, i);
                      }}
                      aria-label="View lead details"
                    >
                      <Eye className="w-3.5 h-3.5" />
                    </Button>
                  </Tip>
                  <DropdownMenu>
                    {/* Nested Slot composition: Radix `asChild` from both
                        TooltipTrigger and DropdownMenuTrigger compose via
                        their internal Slot wrappers, so the Button receives
                        both sets of handlers/refs. Direct ordering matters:
                        Tooltip wraps Dropdown so hover fires the tip and
                        click fires the menu. */}
                    <Tooltip>
                      <TooltipTrigger asChild>
                        <DropdownMenuTrigger asChild>
                          <Button
                            variant="ghost"
                            size="sm"
                            className="h-7 w-7 p-0 text-primary hover:bg-primary/10"
                            onClick={(e) => e.stopPropagation()}
                            aria-label="Navigate to this stop"
                          >
                            <Navigation className="w-3.5 h-3.5 fill-current" />
                          </Button>
                        </DropdownMenuTrigger>
                      </TooltipTrigger>
                      <TooltipContent side="top" className="text-[11px] py-1 px-2">
                        Open in Google Maps or Apple Maps
                      </TooltipContent>
                    </Tooltip>
                    <DropdownMenuContent align="end" className="w-44" onClick={(e) => e.stopPropagation()}>
                      <DropdownMenuItem
                        onClick={(e) => { e.stopPropagation(); openGoogleMaps(stop); }}
                      >
                        <span className="text-sm">Google Maps</span>
                      </DropdownMenuItem>
                      <DropdownMenuItem
                        onClick={(e) => { e.stopPropagation(); openAppleMaps(stop); }}
                      >
                        <span className="text-sm">Apple Maps</span>
                      </DropdownMenuItem>
                    </DropdownMenuContent>
                  </DropdownMenu>
                  {/* Fix-pin button — always available so agents can re-geocode
                      ANY stop they think looks wrong. While THIS stop is being
                      edited (drag-to-fix open on the map), the icon swaps to a
                      pulsing pencil + amber background so the agent can see at
                      a glance which row corresponds to the pin they're moving. */}
                  {!isDone && onAutoFixPin && (
                    <Tip label={isEditing ? "Editing this pin — drag it on the map or type the address" : "Re-check this address & fix the pin"}>
                      <Button
                        variant="ghost"
                        size="sm"
                        className={`h-7 w-7 p-0 disabled:opacity-50 ${
                          isEditing
                            ? "bg-amber-100 text-amber-700 ring-2 ring-amber-400 animate-pulse"
                            : "text-amber-600 hover:bg-amber-50"
                        }`}
                        disabled={autoFixingLeadId === stop.leadId}
                        onClick={async (e) => {
                          e.stopPropagation();
                          if (!onAutoFixPin || autoFixingLeadId === stop.leadId) return;
                          setAutoFixingLeadId(stop.leadId);
                          try { await onAutoFixPin(stop.leadId); }
                          finally { setAutoFixingLeadId(null); }
                        }}
                        aria-label={isEditing ? "Currently editing this pin" : "Re-check pin location"}
                      >
                        {autoFixingLeadId === stop.leadId ? (
                          <Loader2 className="w-3.5 h-3.5 animate-spin" />
                        ) : (
                          <Pencil className="w-3.5 h-3.5" />
                        )}
                      </Button>
                    </Tip>
                  )}
                  {!isDone && (
                    <>
                      {/* Status group: not-home, visited, skip — the three
                          door-knock outcomes the agent fires for the current
                          stop. Grouped together so muscle memory stays put. */}
                      <Tip label="Mark as Not Home">
                        <Button
                          variant="ghost"
                          size="sm"
                          className="h-7 w-7 p-0 text-amber-500 hover:bg-amber-50 hover:text-amber-600"
                          onClick={(e) => { e.stopPropagation(); onMarkNotHome?.(stop.leadId); }}
                          aria-label="Mark as not home"
                        >
                          <DoorOpen className="w-3.5 h-3.5" />
                        </Button>
                      </Tip>
                      <Tip label="Mark stop visited">
                        <Button
                          variant="ghost"
                          size="sm"
                          className="h-7 w-7 p-0 text-emerald-600 hover:bg-emerald-50 hover:text-emerald-700"
                          onClick={(e) => { e.stopPropagation(); onMarkVisited(stop.leadId); }}
                          aria-label="Mark stop as visited"
                        >
                          <Check className="w-3.5 h-3.5" />
                        </Button>
                      </Tip>
                      <Tip label="Skip this stop">
                        <Button
                          variant="ghost"
                          size="sm"
                          className="h-7 w-7 p-0 text-gray-400 hover:bg-gray-100 hover:text-gray-600"
                          onClick={(e) => { e.stopPropagation(); onMarkSkipped(stop.leadId); }}
                          aria-label="Skip this stop"
                        >
                          <SkipForward className="w-3.5 h-3.5" />
                        </Button>
                      </Tip>
                      {/* Pipeline + tag dropdown toggle — moved to the END of
                          the row so it's a separate "more / details" group
                          and doesn't break up the see → fix → status flow.
                          Slight left-margin separates it visually from the
                          status icons. */}
                      <Tip label="Pipeline status & quick tags">
                        <Button
                          variant="ghost"
                          size="sm"
                          className="h-7 w-7 p-0 ml-1 text-gray-400 hover:bg-gray-100 hover:text-gray-600"
                          onClick={(e) => {
                            e.stopPropagation();
                            setExpandedStopId(isExpanded ? null : stop.leadId);
                          }}
                          aria-label="Set pipeline status or field tag"
                        >
                          <ChevronDown className={`w-3.5 h-3.5 transition-transform ${isExpanded ? "rotate-180" : ""}`} />
                        </Button>
                      </Tip>
                    </>
                  )}
                </div>
                </div>{/* close stop-info column */}
              </div>{/* close row */}

              {/* Pipeline + field tag selector (expanded) */}
              {isExpanded && !isDone && (
                <div className="px-4 pb-3 pt-0 pl-14 space-y-2">
                  {/* Pipeline */}
                  <div>
                    <p className="text-[9px] text-gray-400 uppercase tracking-wider mb-1">Pipeline</p>
                    <div className="flex flex-wrap gap-1.5">
                      {PIPELINE_OPTIONS.map((opt) => (
                        <button
                          key={opt.value}
                          onClick={(e) => { e.stopPropagation(); handlePipelineSelect(stop.leadId, opt.value); }}
                          className={`text-[10px] font-semibold px-2 py-1 rounded-full transition-colors ${getStatusColor(opt.value)} hover:opacity-80`}
                          title={`Set pipeline status to ${opt.label}`}
                          aria-label={`Set pipeline status to ${opt.label}`}
                        >
                          {opt.label}
                        </button>
                      ))}
                    </div>
                  </div>
                  {/* Field tags */}
                  <div>
                    <p className="text-[9px] text-gray-400 uppercase tracking-wider mb-1">Quick Tag</p>
                    <div className="flex flex-wrap gap-1.5">
                      {FIELD_TAGS.map((tag) => (
                        <button
                          key={tag.value}
                          onClick={(e) => { e.stopPropagation(); onSetFieldTag?.(stop.leadId, tag.value); setExpandedStopId(null); }}
                          className={`text-[10px] font-semibold px-2 py-1 rounded-full transition-colors ${tag.color} hover:opacity-80 ${stop.fieldTag === tag.value ? "ring-1 ring-primary" : ""}`}
                          title={`Tag this stop as "${tag.label}"`}
                          aria-label={`Tag this stop as ${tag.label}`}
                        >
                          {tag.label}
                        </button>
                      ))}
                    </div>
                    {/* Custom tag input */}
                    <CustomTagInput onAddTag={(tag) => { onSetFieldTag?.(stop.leadId, tag); setExpandedStopId(null); }} />
                  </div>
                </div>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
}

// ── Custom Tag Input ─────────────────────────────────────────────

function CustomTagInput({ onAddTag }: { onAddTag: (tag: string) => void }) {
  const [isOpen, setIsOpen] = useState(false);
  const [value, setValue] = useState("");
  const inputRef = useRef<HTMLInputElement>(null);

  if (!isOpen) {
    return (
      <button
        onClick={(e) => { e.stopPropagation(); setIsOpen(true); setTimeout(() => inputRef.current?.focus(), 50); }}
        className="mt-1.5 flex items-center gap-1 text-[9px] text-gray-400 hover:text-gray-600 transition-colors"
        title="Add a custom tag for this stop"
        aria-label="Add custom tag"
      >
        <Plus className="h-3 w-3" />
        Custom tag
      </button>
    );
  }

  return (
    <div className="mt-1.5 flex items-center gap-1" onClick={(e) => e.stopPropagation()}>
      <input
        ref={inputRef}
        value={value}
        onChange={(e) => setValue(e.target.value)}
        placeholder="Tag name..."
        className="h-6 w-24 rounded border border-gray-200 px-2 text-[10px] focus:outline-none focus:ring-1 focus:ring-primary"
        onKeyDown={(e) => {
          if (e.key === "Enter" && value.trim()) {
            onAddTag(value.trim().toLowerCase().replace(/\s+/g, "_"));
            setValue("");
            setIsOpen(false);
          }
          if (e.key === "Escape") {
            setValue("");
            setIsOpen(false);
          }
        }}
      />
      <button
        onClick={() => {
          if (value.trim()) {
            onAddTag(value.trim().toLowerCase().replace(/\s+/g, "_"));
            setValue("");
            setIsOpen(false);
          }
        }}
        className="h-6 w-6 rounded bg-primary/10 flex items-center justify-center hover:bg-primary/20 transition-colors"
        title="Save tag"
        aria-label="Save custom tag"
      >
        <Check className="h-3 w-3 text-primary" />
      </button>
    </div>
  );
}
